import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../primitives/alert-dialog.js';
import { Alert, AlertDescription } from '../primitives/alert.js';
import { Spinner } from '../primitives/spinner.js';
import { buttonVariants } from '../primitives/button.js';
import { cn } from '../lib/cn.js';
import { isResourceRequestError } from './client.js';
import type { ConfirmSpec } from './types.js';

interface PendingConfirm {
  spec: ConfirmSpec;
  tone: 'default' | 'danger';
  run: () => void | Promise<void>;
}

export function messageOf(error: unknown): string {
  if (isResourceRequestError(error)) return error.error.message;
  if (error instanceof Error) return error.message;
  return 'Operazione non riuscita.';
}

/**
 * One confirmation surface for the whole kit. Row actions, bulk actions and the
 * detail screen's delete all go through it, so a destructive action can never
 * ship with a bespoke — or missing — "are you sure".
 *
 * A failed action keeps the dialog open and says why. Closing on failure was the
 * first version, and it is indistinguishable from success: the user watches the
 * dialog disappear and assumes the record is gone.
 */
export function useConfirm() {
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  const [running, setRunning] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const confirm = React.useCallback(
    (spec: ConfirmSpec, tone: 'default' | 'danger', run: () => void | Promise<void>) => {
      setError(null);
      setPending({ spec, tone, run });
    },
    [],
  );

  const dialog = (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (open || running) return;
        setPending(null);
        setError(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{pending?.spec.title}</AlertDialogTitle>
          {pending?.spec.description && (
            <AlertDialogDescription>{pending.spec.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>

        {error && (
          <Alert tone="danger">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={running}>
            {pending?.spec.cancelLabel ?? 'Annulla'}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={running}
            className={cn(pending?.tone === 'danger' && buttonVariants({ variant: 'danger' }))}
            onClick={(event) => {
              // The dialog closes on its own click handler; it has to stay open
              // while the action runs, and afterwards if the action failed.
              event.preventDefault();
              if (!pending) return;
              setRunning(true);
              setError(null);
              // `run()` is invoked inside the chain, not before it: an action
              // that throws synchronously would otherwise escape the catch and
              // leave the dialog spinning.
              void Promise.resolve()
                .then(() => pending.run())
                .then(() => {
                  setPending(null);
                })
                .catch((cause: unknown) => {
                  setError(messageOf(cause));
                })
                .finally(() => {
                  setRunning(false);
                });
            }}
          >
            {running && <Spinner className="text-current" />}
            {pending?.spec.confirmLabel ?? 'Conferma'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
}

export function resolveConfirm<TTarget>(
  spec: ConfirmSpec | ((target: TTarget) => ConfirmSpec) | undefined,
  target: TTarget,
): ConfirmSpec | undefined {
  if (!spec) return undefined;
  return typeof spec === 'function' ? spec(target) : spec;
}
